// src/contexts/RBACContext.js
import React, { createContext, useContext, useState, useEffect } from "react";
import { useAuth } from "./AuthContext";
import { api } from "../api";

const RBACContext = createContext(null);

// A failed permissions request (backend restarting or still loading its
// face-recognition models, a network blip, a timeout) is retried with this
// backoff until it succeeds. Without it one failure left every guarded page on
// "Loading permissions..." until the user refreshed by hand.
const RETRY_DELAYS_MS = [1000, 2000, 3000, 5000];

const getRetryDelay = (attempt) =>
  RETRY_DELAYS_MS[Math.min(attempt, RETRY_DELAYS_MS.length - 1)];

// Only request failures that can clear up on their own are retried: no
// response at all, a timeout, rate limiting or a server error. Any other 4xx
// (401 is already turned into a redirect to /login by the api client) and
// errors thrown while reading the response are final.
const isRetryable = (err) => {
  if (!err?.isAxiosError) return false;
  const status = err.response?.status;
  if (!status) return true;
  return status === 408 || status === 429 || status >= 500;
};

export const RBACProvider = ({ children }) => {
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();
  const [permissions, setPermissions] = useState({});
  const [allRoles, setAllRoles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Stay "loading" until AuthContext has read the stored session; reporting
    // loaded before that briefly rendered pages with no permissions.
    if (authLoading) return undefined;

    if (!isAuthenticated || !user?.id) {
      setIsLoading(false);
      return undefined;
    }

    // Set when the user/role changes or the provider unmounts, so a late
    // response for the previous role can't overwrite the current one.
    let cancelled = false;
    let retryTimer = null;
    let attempt = 0;

    const fetchPermissions = async () => {
      try {
        const response = await api.get(
          `/api/roles/getAllRolePermissions`
        );
        if (cancelled) return;

        // Check if the response has the expected format
        if (response.data) {
          // If the response is directly the permissions array
          if (Array.isArray(response.data)) {
            // Format the permissions into our expected structure
            const formattedPermissions = {};
            response.data.forEach((permission) => {
              formattedPermissions[permission.module] = permission.actions;
            });

            setPermissions(formattedPermissions);
            setAllRoles([{ roleName: user.role, permissions: response.data }]);
          }
          // If the response has a success property and roles array
          else if (
            response.data.success &&
            Array.isArray(response.data.roles)
          ) {
            setAllRoles(response.data.roles);

            // Get user role from auth context
            const userRole = user.role.toLowerCase();

            // Find matching role in API response
            const roleData = response.data.roles.find(
              (role) => role.roleName.toLowerCase() === userRole
            );

            if (roleData) {
              // Transform permissions to our format for easier access
              const formattedPermissions = {};

              roleData.permissions.forEach((permission) => {
                formattedPermissions[permission.module] = permission.actions;
              });

              setPermissions(formattedPermissions);
            } else {
              console.warn(`Role '${userRole}' not found in permissions data`);
              setPermissions({});
            }
          } else {
            console.error("Unexpected API response format:", response.data);
            setPermissions({});
          }
        } else {
          console.error("API returned empty response");
          setPermissions({});
        }

        setError(null);
        setIsLoading(false);
      } catch (err) {
        if (cancelled) return;
        if (attempt === 0) console.error("Failed to fetch permissions:", err);
        setError("Failed to load permissions");
        // Keep the same (empty) object across retries so consumers that
        // depend on `permissions` don't re-run on every attempt.
        setPermissions((prev) => (Object.keys(prev).length ? {} : prev));
        setIsLoading(false);

        if (isRetryable(err)) {
          retryTimer = setTimeout(fetchPermissions, getRetryDelay(attempt));
          attempt += 1;
        }
      }
    };

    setIsLoading(true);
    fetchPermissions();

    return () => {
      cancelled = true;
      clearTimeout(retryTimer);
    };
  }, [user?.id, user?.role, isAuthenticated, authLoading]);

  const hasPermission = (module, action) => {
    if (!permissions || !permissions[module]) return false;
    return permissions[module].includes(action);
  };

  const hasModuleAccess = (module) => {
    return !!permissions[module] && permissions[module].length > 0;
  };

  const getAllModules = () => {
    return Object.keys(permissions);
  };

  const getModuleActions = (module) => {
    return permissions[module] || [];
  };

  const getRoles = () => {
    return allRoles;
  };

  const getRolePermissions = (roleName) => {
    const role = allRoles.find(
      (r) => r.roleName.toLowerCase() === roleName.toLowerCase()
    );
    return role ? role.permissions : [];
  };

  return (
    <RBACContext.Provider
      value={{
        permissions,
        allRoles,
        isLoading,
        error,
        hasPermission,
        hasModuleAccess,
        getAllModules,
        getModuleActions,
        getRoles,
        getRolePermissions,
      }}
    >
      {children}
    </RBACContext.Provider>
  );
};

export const useRBAC = () => {
  const context = useContext(RBACContext);
  if (!context) {
    throw new Error("useRBAC must be used within an RBACProvider");
  }
  return context;
};
